import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Frown, Package, RefreshCw } from "lucide-react";
import { useCategory, useProducts } from "@/features/catalog/hooks";
import { ProductCard } from "@/components/catalog/ProductCard";
import { ProductCardSkeleton } from "@/components/catalog/Skeletons";
import { resolveCategoryIcon } from "@/features/catalog/iconMap";
import { SubmitButton } from "@/components/form/SubmitButton";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";
import type { ProductSort } from "@/features/catalog/types";

const SORT_OPTIONS: Array<{ value: ProductSort; label: string }> = [
    { value: "popular", label: "Popular" },
    { value: "price_asc", label: "Price: low to high" },
    { value: "price_desc", label: "Price: high to low" },
    { value: "discount", label: "Discount" },
    { value: "rating", label: "Top rated" },
];

/**
 * CategoryDetail — banner + product grid + sort selector.
 * Sort UI keeps a local state; server does the actual sorting.
 */
export default function CategoryDetail() {
    const { slug = "" } = useParams<{ slug: string }>();
    const [sort, setSort] = useState<ProductSort>("popular");

    const categoryQuery = useCategory(slug);
    const productsQuery = useProducts({ category: slug, sort, page_size: 60 });

    if (categoryQuery.isError) {
        return <CategoryError />;
    }

    const category = categoryQuery.data;
    const Icon = category ? resolveCategoryIcon(category.icon) : Package;

    return (
        <div data-testid="category-detail-page" className="tsgo-page">
            {/* Banner */}
            <section className="tsgo-container pt-4 sm:pt-6">
                <div
                    className={cn(
                        "relative overflow-hidden rounded-3xl p-6 shadow-soft sm:p-10",
                        category
                            ? `bg-gradient-to-br ${category.banner_gradient}`
                            : "bg-muted",
                    )}
                    data-testid="category-banner"
                >
                    <div className="pointer-events-none absolute inset-0 opacity-30" aria-hidden>
                        <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full bg-white/30 blur-3xl" />
                        <div className="absolute -bottom-16 -left-12 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
                    </div>

                    <Link
                        to={ROUTES.categories}
                        className="relative z-10 inline-flex items-center gap-1 text-xs font-semibold text-white/85 transition hover:text-white"
                        data-testid="category-back"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> All categories
                    </Link>

                    <div className="relative z-10 mt-3 flex items-center gap-4">
                        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 backdrop-blur">
                            <Icon className="h-7 w-7" />
                        </span>
                        <div>
                            <h1
                                className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl"
                                data-testid="category-name"
                            >
                                {category?.name ?? "Loading…"}
                            </h1>
                            <p className="mt-1 max-w-lg text-sm text-white/90 sm:text-base">
                                {category?.description ?? ""}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter + sort row */}
            <section className="tsgo-container mt-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground" data-testid="category-count">
                    {productsQuery.isLoading
                        ? "Loading products…"
                        : `${productsQuery.data?.total ?? 0} products`}
                </p>
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Sort products">
                    {SORT_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => setSort(opt.value)}
                            data-testid={`sort-${opt.value}`}
                            className={cn(
                                "rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                                sort === opt.value
                                    ? "border-brand bg-brand text-white"
                                    : "border-border bg-card text-foreground/70 hover:border-brand/40 hover:text-foreground",
                            )}
                            aria-pressed={sort === opt.value}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Product grid */}
            <section className="tsgo-container mt-4">
                {productsQuery.isLoading ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <ProductCardSkeleton key={i} />
                        ))}
                    </div>
                ) : productsQuery.isError ? (
                    <ProductLoadError onRetry={() => productsQuery.refetch()} />
                ) : (productsQuery.data?.items ?? []).length === 0 ? (
                    <EmptyState />
                ) : (
                    <div
                        data-testid="category-product-grid"
                        className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
                    >
                        {productsQuery.data?.items.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function CategoryError() {
    return (
        <div className="tsgo-container flex min-h-[60vh] items-center justify-center py-16">
            <div className="max-w-md rounded-2xl border border-border/70 bg-card p-8 text-center">
                <Frown className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                <h2 className="text-xl font-semibold">Category not found</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                    The category you're looking for doesn't exist or has been removed.
                </p>
                <div className="mt-5">
                    <SubmitButton
                        type="button"
                        variant="outline"
                        fullWidth={false}
                        onClick={() => (window.location.href = ROUTES.categories)}
                    >
                        Back to categories
                    </SubmitButton>
                </div>
            </div>
        </div>
    );
}

function ProductLoadError({ onRetry }: { onRetry: () => void }) {
    return (
        <div className="mx-auto max-w-md rounded-2xl border border-dashed border-error/40 bg-error/5 p-6 text-center">
            <p className="font-semibold">Couldn't load products</p>
            <div className="mt-3">
                <SubmitButton
                    type="button"
                    variant="outline"
                    fullWidth={false}
                    onClick={onRetry}
                >
                    <RefreshCw className="mr-1.5 h-4 w-4" /> Retry
                </SubmitButton>
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="mx-auto max-w-md rounded-2xl border border-dashed border-border/70 bg-muted/30 p-8 text-center">
            <Package className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-semibold">No products yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
                We're stocking this category — check back soon.
            </p>
        </div>
    );
}
